import { Link } from 'react-router-dom'
import { ShoppingCart, Users, LogOut, Bot, ChevronRight, Loader2 } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { dashboard } from '@/utils/api'
import { useAuth } from '@/context/AuthContext'
import { formatDate, getStatusLabel } from '@/utils/formatters'

interface DashboardData {
  ordersCount?: number
  orders?: { id: number; project_name?: string; items_count: number; status: string }[]
  presentCount?: number
  teamMembers?: { id: number; name: string; isPresent: boolean }[]
  absences?: { name: string; type: string }[]
}

const statusVariant = (s: string) => s === 'submitted' ? 'warning' as const : s === 'ordered' ? 'success' as const : 'secondary' as const

export default function BauleiterDashboard() {
  const { user, logout } = useAuth()

  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ['bauleiter-dashboard'],
    queryFn: () => dashboard.getBauleiter().then(r => r.data),
  })

  if (isLoading) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary-500" /></div>

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-primary-500 p-4 text-white">
        <div className="mx-auto flex max-w-lg items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold">Hallo, {user?.display_name}</h1>
            <p className="text-sm text-primary-200">{formatDate(new Date(), { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          </div>
          <button onClick={logout} className="rounded-lg p-2 transition-colors hover:bg-primary-600"><LogOut className="h-5 w-5" /></button>
        </div>
      </header>

      <div className="mx-auto max-w-lg space-y-4 p-4">
        <Card className="bg-gradient-to-r from-primary-500 to-primary-600 text-white">
          <Link to="/bauleiter/ki/zaehler" className="block">
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-white/20 p-2"><Bot className="h-6 w-6" /></div>
                <div><h2 className="font-semibold">K.I. Funktionen</h2><p className="text-sm text-white/80">Zaehler erfassen</p></div>
              </div>
              <ChevronRight className="h-6 w-6 text-white/60" />
            </CardContent>
          </Link>
        </Card>

        <Card><CardContent className="p-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-accent-50 p-2"><ShoppingCart className="h-6 w-6 text-accent-500" /></div>
              <div><h2 className="font-semibold">Meine Bestellungen</h2><p className="text-sm text-muted-foreground">{data?.ordersCount || 0} offen</p></div>
            </div>
          </div>
          {data?.orders && data.orders.length > 0 ? (
            <div className="space-y-2">{data.orders.slice(0, 3).map(o => (
              <div key={o.id} className="flex items-center justify-between rounded-lg bg-muted p-3">
                <div><p className="text-sm font-medium">{o.project_name || 'Kein Bauvorhaben'}</p><p className="text-xs text-muted-foreground">{o.items_count} Positionen</p></div>
                <Badge variant={statusVariant(o.status)}>{getStatusLabel(o.status)}</Badge>
              </div>
            ))}</div>
          ) : <p className="py-4 text-center text-sm text-muted-foreground">Keine aktuellen Bestellungen</p>}
        </CardContent></Card>

        <Card><CardContent className="p-4">
          <div className="mb-4 flex items-center gap-3">
            <div className="rounded-lg bg-green-50 p-2"><Users className="h-6 w-6 text-green-600" /></div>
            <div><h2 className="font-semibold">Team heute</h2><p className="text-sm text-muted-foreground">{data?.presentCount || 0} anwesend</p></div>
          </div>
          {data?.teamMembers && data.teamMembers.length > 0 ? (
            <div className="grid grid-cols-2 gap-2">{data.teamMembers.map(m => (
              <div key={m.id} className="flex items-center gap-2 rounded bg-muted p-2">
                <span className={`h-2 w-2 rounded-full ${m.isPresent ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="truncate text-sm">{m.name}</span>
              </div>
            ))}</div>
          ) : <p className="py-4 text-center text-sm text-muted-foreground">Keine Teammitglieder</p>}
        </CardContent></Card>

        {data?.absences && data.absences.length > 0 && (
          <Card><CardContent className="p-4">
            <h2 className="mb-3 font-semibold">Abwesenheiten heute</h2>
            <div className="space-y-2">{data.absences.map((a, i) => (
              <div key={i} className="flex items-center justify-between rounded bg-muted p-2">
                <span className="text-sm">{a.name}</span>
                <Badge variant={a.type === 'krank' ? 'destructive' : 'info'}>{a.type === 'krank' ? 'Krank' : 'Urlaub'}</Badge>
              </div>
            ))}</div>
          </CardContent></Card>
        )}
      </div>
    </div>
  )
}
